import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SHORT_ACTIVATE_DELAY = 0;
const LONG_ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });
    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #state = {
    activeItem: null,
  };

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (this.#state.activeItem) {
      this.#shortDebouncedActivateHandler(event);
    } else {
      this.#longDebouncedActivateHandler(event);
    }
  };

  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();
    this.dispatchEvent(new MegaMenuHoverEvent());
    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;
    let item = findMenuItem(event.target);
    if (!item || item == this.#state.activeItem) return;

    // === MODIFIKASI DIMULAI DI SINI ===
    // Cek apakah submenu yang akan dibuka adalah submenu biasa
    const submenu = findSubmenu(item);
    if (submenu && submenu.classList.contains('simple-submenu')) {
      // Jika ya, tambahkan class 'penanda' ke elemen <header-menu>
      this.classList.add('simple-menu-is-active');
    } else {
      // Jika tidak (berarti ini mega menu), hapus class penanda
      this.classList.remove('simple-menu-is-active');
    }
    // === MODIFIKASI SELESAI ===

    const isDefaultSlot = event.target.slot === '';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;
    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;
    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
  };

  #shortDebouncedActivateHandler = debounce(this.#activateHandler, SHORT_ACTIVATE_DELAY);
  #longDebouncedActivateHandler = debounce(this.#activateHandler, LONG_ACTIVATE_DELAY);

  deactivate(event) {
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();
    if (!(event.target instanceof Element)) return;
    const item = findMenuItem(event.target);
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    // === MODIFIKASI: Hapus class penanda saat menu ditutup ===
    this.classList.remove('simple-menu-is-active');

    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';
    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }
  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}